import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentCollectionPortal = ({ candidate, onClose }) => {
  const [documents, setDocuments] = useState([
    {
      id: 1,
      name: 'PNG Citizenship Certificate',
      description: 'Valid PNG citizenship documentation',
      required: true,
      status: 'uploaded',
      uploadedDate: '2026-01-15',
      fileName: 'citizenship_john_kila.pdf'
    },
    {
      id: 2,
      name: 'Employment Contract',
      description: 'Signed employment agreement',
      required: true,
      status: 'uploaded',
      uploadedDate: '2026-01-16',
      fileName: 'employment_contract_signed.pdf'
    },
    {
      id: 3,
      name: 'NASFUND Registration Form',
      description: 'Completed NASFUND member registration',
      required: true,
      status: 'uploaded',
      uploadedDate: '2026-01-17',
      fileName: 'nasfund_registration.pdf'
    },
    {
      id: 4,
      name: 'Tax Declaration (TIN)',
      description: 'PNG Tax Identification Number certificate',
      required: true,
      status: 'pending',
      uploadedDate: null,
      fileName: null
    },
    {
      id: 5,
      name: 'Bank Account Details',
      description: 'Bank statement or account verification',
      required: true,
      status: 'pending',
      uploadedDate: null,
      fileName: null
    },
    {
      id: 6,
      name: 'Educational Certificates',
      description: 'Academic qualifications and certifications',
      required: false,
      status: 'pending',
      uploadedDate: null,
      fileName: null
    }
  ]);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e, documentId) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    const files = e?.dataTransfer?.files;
    if (files && files?.length > 0) {
      handleFileUpload(files?.[0], documentId);
    }
  };

  const handleFileInput = (e, documentId) => {
    const files = e?.target?.files;
    if (files && files?.length > 0) {
      handleFileUpload(files?.[0], documentId);
    }
  };

  const handleFileUpload = (file, documentId) => {
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes?.includes(file?.type)) {
      alert('Only PDF, JPG, and PNG files are allowed');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file?.size > maxSize) {
      alert('File size must be less than 5MB');
      return;
    }

    // Update document status
    setDocuments(prev =>
      prev?.map(doc =>
        doc?.id === documentId
          ? {
              ...doc,
              status: 'uploaded',
              uploadedDate: new Date()?.toISOString()?.split('T')?.[0],
              fileName: file?.name
            }
          : doc
      )
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'uploaded':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'pending':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'rejected':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const uploadedCount = documents?.filter(d => d?.status === 'uploaded')?.length;
  const totalRequired = documents?.filter(d => d?.required)?.length;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-emerald-100 p-2 rounded-lg">
            <Icon name="Upload" size={24} className="text-emerald-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Document Collection Portal</h2>
            <p className="text-sm text-gray-600">{candidate?.employeeName}</p>
          </div>
        </div>
        <Button onClick={onClose} variant="outline" size="sm">
          <Icon name="X" size={16} className="mr-1" />
          Close
        </Button>
      </div>

      {/* Progress Summary */}
      <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-lg p-4 mb-6 border border-emerald-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-emerald-700">
            Document Collection Progress
          </span>
          <span className="text-sm font-bold text-emerald-900">
            {uploadedCount} / {totalRequired} Required
          </span>
        </div>
        <div className="w-full bg-emerald-200 rounded-full h-2">
          <div
            className="bg-emerald-600 h-2 rounded-full transition-all"
            style={{ width: `${(uploadedCount / totalRequired) * 100}%` }}
          />
        </div>
      </div>

      {/* Document List */}
      <div className="space-y-3">
        {documents?.map(doc => (
          <div key={doc?.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base font-semibold text-gray-900">{doc?.name}</h3>
                  {doc?.required && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                      Required
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-2">{doc?.description}</p>
                {doc?.fileName && (
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Icon name="File" size={14} />
                    {doc?.fileName}
                  </p>
                )}
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(
                  doc?.status
                )}`}
              >
                {doc?.status?.toUpperCase()}
              </span>
            </div>

            {/* Upload Area */}
            {doc?.status === 'pending' && (
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={(e) => handleDrop(e, doc?.id)}
                className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                  dragActive
                    ? 'border-emerald-500 bg-emerald-50' :'border-gray-300 bg-gray-50'
                }`}
              >
                <Icon name="Upload" size={24} className="mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 mb-2">
                  Drag and drop file here or click to browse
                </p>
                <input
                  type="file"
                  id={`file-input-${doc?.id}`}
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileInput(e, doc?.id)}
                />
                <label htmlFor={`file-input-${doc?.id}`}>
                  <Button as="span" size="sm" variant="outline">
                    Browse Files
                  </Button>
                </label>
                <p className="text-xs text-gray-500 mt-2">PDF, JPG, PNG (Max 5MB)</p>
              </div>
            )}

            {/* Uploaded File Actions */}
            {doc?.status === 'uploaded' && (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline">
                  <Icon name="Eye" size={14} className="mr-1" />
                  View
                </Button>
                <Button size="sm" variant="outline">
                  <Icon name="Download" size={14} className="mr-1" />
                  Download
                </Button>
                <Button size="sm" variant="outline" className="text-red-600">
                  <Icon name="Trash2" size={14} className="mr-1" />
                  Remove
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocumentCollectionPortal;